<script setup>
/**
 * AppTableWidget — kompakte eingebettete Tabelle
 *
 * Für: Detail-Seiten-Tabs, Dashboard-Cards, Subkomponenten
 * Kein Spalten-Picker, kein Persistence — alle Spalten immer sichtbar.
 *
 * Props:
 *   columns      Array    [{ field, header, sortable? }]
 *   data         Array
 *   total        Number   Gesamtanzahl (lazy)
 *   loading      Boolean
 *   lazy         Boolean
 *   searchable   Boolean  Suchfeld anzeigen (default: false)
 *   paginated    Boolean  Paginierung anzeigen (default: true)
 *   actions      Array    Aktions-Definitionen (gleiche Struktur wie AppTable)
 *   expandable   Boolean  Zeilen aufklappbar (default: false)
 *   emptyText    String
 *   defaultRows  Number   (default: 25)
 *   rowsOptions  Array
 *   dataKey      String   (default: 'id')
 *   title        String   Optionaler Kartenheader
 *   rowClickable Boolean
 *
 * Emits:
 *   refresh
 *   query-change  { search, page, limit, sortField, sortOrder }
 *   row-click
 *
 * Slots:
 *   toolbar        → rechte Seite der Header-Leiste
 *   cell-{field}   → custom Zell-Rendering, { row }
 *   expansion      → aufgeklappter Zeileninhalt, { row }
 *   empty
 */
import { ref, computed, watch } from 'vue'
import { useI18n } from '@/i18n'
import DataTable from 'primevue/datatable'
import Column    from 'primevue/column'
import InputText from 'primevue/inputtext'
import IconField from 'primevue/iconfield'
import InputIcon from 'primevue/inputicon'
import Button    from 'primevue/button'
import Menu      from 'primevue/menu'

const { t } = useI18n()

const props = defineProps({
  columns:     { type: Array,   required: true },
  data:        { type: Array,   default: () => [] },
  total:       { type: Number,  default: 0 },
  loading:     { type: Boolean, default: false },
  lazy:        { type: Boolean, default: false },
  searchable:  { type: Boolean, default: false },
  paginated:   { type: Boolean, default: true },
  actions:     { type: Array,   default: () => [] },
  expandable:  { type: Boolean, default: false },
  emptyText:   { type: String,  default: '' },
  defaultRows: { type: Number,  default: 25 },
  rowsOptions: { type: Array,   default: () => [10, 25, 50, 100] },
  dataKey:     { type: String,  default: 'id' },
  title:       { type: String,  default: '' },
  rowClickable:{ type: Boolean, default: false },
})

const emit = defineEmits(['refresh', 'query-change', 'row-click'])

// ─── Suche ──────────────────────────────────────────────────────────────────
const search = ref('')
let searchTimer = null
watch(search, () => {
  clearTimeout(searchTimer)
  searchTimer = setTimeout(() => {
    dtFirst.value = 0
    if (props.lazy) emitQuery()
  }, 300)
})

const filtered = computed(() => {
  if (props.lazy) return props.data
  const term = search.value.trim().toLowerCase()
  if (!term) return props.data
  return props.data.filter(row =>
    props.columns.some(c => String(row[c.field] ?? '').toLowerCase().includes(term))
  )
})

// ─── Paginierung / Sort ──────────────────────────────────────────────────────
const dtFirst     = ref(0)
const rowsPerPage = ref(props.defaultRows)
const sortField   = ref(null)
const sortOrder   = ref(null)

function onPage(e) {
  dtFirst.value     = e.first
  rowsPerPage.value = e.rows
  if (props.lazy) emitQuery()
}

function onSort(e) {
  sortField.value = e.sortField ?? null
  sortOrder.value = e.sortOrder === 1 ? 'asc' : (e.sortOrder === -1 ? 'desc' : null)
  dtFirst.value   = 0
  if (props.lazy) emitQuery()
}

function emitQuery() {
  emit('query-change', {
    search:    search.value.trim(),
    page:      Math.floor(dtFirst.value / rowsPerPage.value) + 1,
    limit:     rowsPerPage.value,
    sortField: sortField.value,
    sortOrder: sortOrder.value,
  })
}

const totalRecords = computed(() => props.lazy ? props.total : filtered.value.length)

// ─── Expandable Rows ─────────────────────────────────────────────────────────
const expandedRows = ref([])

// ─── Auswahl & Aktionen ──────────────────────────────────────────────────────
const selectedRows   = ref([])
const actionsMenuRef = ref(null)

const computedActions = computed(() => props.actions.filter(Boolean))
const hasActions      = computed(() => computedActions.value.length > 0)

function itemDisabled(a) {
  if (a.requiresSingle) return selectedRows.value.length !== 1
  return selectedRows.value.length === 0
}

const menuItems = computed(() => {
  const items = []
  for (const a of computedActions.value) {
    if (a.separator && items.length) items.push({ separator: true })
    items.push({
      label:    a.label,
      icon:     a.icon,
      class:    a.severity === 'danger' ? 'action-item-danger' : undefined,
      disabled: itemDisabled(a),
      command:  () => {
        a.command(selectedRows.value)
        selectedRows.value = []
      },
    })
  }
  return items
})

watch(() => props.data, () => { selectedRows.value = [] })
</script>

<template>
  <div class="atw">
    <!-- ── Header (optional) ─────────────────────────────────── -->
    <div v-if="title || searchable || hasActions || $slots.toolbar" class="atw-header">
      <span v-if="title" class="atw-title">{{ title }}</span>
      <div class="atw-header-right">
        <!-- Aktionen-Dropdown -->
        <template v-if="hasActions">
          <span v-if="selectedRows.length" class="atw-selection-count">
            {{ selectedRows.length }} {{ t('common.selected') }}
          </span>
          <Button
            icon="pi pi-chevron-down"
            :label="t('common.actions')"
            size="small"
            severity="secondary"
            outlined
            @click="actionsMenuRef?.toggle($event)"
          />
          <Menu ref="actionsMenuRef" :model="menuItems" popup>
            <template #item="{ item, props: iProps }">
              <a v-if="!item.separator" v-bind="iProps.action" :class="['action-menu-item', item.class, { 'p-disabled': item.disabled }]">
                <span :class="item.icon" />
                <span class="ml-2">{{ item.label }}</span>
              </a>
            </template>
          </Menu>
        </template>

        <IconField v-if="searchable">
          <InputIcon class="pi pi-search" />
          <InputText v-model="search" :placeholder="t('common.search')" class="atw-search" />
        </IconField>
        <slot name="toolbar" />
        <Button
          icon="pi pi-refresh"
          v-tooltip.top="t('common.refresh')"
          size="small"
          text
          severity="secondary"
          :loading="loading"
          @click="$emit('refresh')"
        />
      </div>
    </div>

    <!-- ── DataTable ─────────────────────────────────────────── -->
    <DataTable
      :value="lazy ? data : filtered"
      :loading="loading"
      :lazy="lazy"
      :totalRecords="totalRecords"
      :dataKey="dataKey"
      v-model:selection="selectedRows"
      :selectionMode="hasActions ? 'multiple' : undefined"
      :paginator="paginated"
      :rows="rowsPerPage"
      :rowsPerPageOptions="paginated ? rowsOptions : undefined"
      :first="dtFirst"
      size="small"
      sortMode="single"
      removableSort
      stripedRows
      scrollable
      responsiveLayout="scroll"
      v-model:expandedRows="expandedRows"
      paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown CurrentPageReport"
      currentPageReportTemplate="{first}–{last} / {totalRecords}"
      :rowClass="rowClickable ? () => 'atw-row-clickable' : undefined"
      @page="onPage"
      @sort="onSort"
      @row-click="$emit('row-click', $event.data)"
    >
      <template #empty>
        <slot name="empty">
          <div class="atw-empty">
            <i class="pi pi-inbox" />
            <span>{{ emptyText || t('common.empty') }}</span>
          </div>
        </slot>
      </template>

      <!-- Expand-Toggle -->
      <Column v-if="expandable" expander style="width:40px" />

      <!-- Checkbox-Spalte -->
      <Column
        v-if="hasActions"
        selectionMode="multiple"
        style="width:40px;padding-right:0"
        :exportable="false"
      />

      <!-- Spalten -->
      <Column
        v-for="col in columns"
        :key="col.field"
        :field="col.field"
        :header="col.header"
        :sortable="col.sortable ?? false"
        :style="col.minWidth ? `min-width:${col.minWidth}` : ''"
      >
        <template #body="{ data: row }">
          <slot :name="`cell-${col.field}`" :row="row">
            {{ row[col.field] }}
          </slot>
        </template>
      </Column>

      <!-- Expansion -->
      <template v-if="expandable" #expansion="{ data: row }">
        <slot name="expansion" :row="row" />
      </template>
    </DataTable>
  </div>
</template>

<style scoped>
.atw {
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

/* ── Header ──────────────────────────────────────────────────── */
.atw-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 12px;
  border-bottom: 1px solid var(--p-content-border-color);
  gap: 8px;
  flex-wrap: wrap;
}
.atw-title {
  font-size: 0.78rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: var(--p-text-muted-color);
}
.atw-header-right {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-left: auto;
}
.atw-search {
  width: 180px;
  height: 30px;
  font-size: 0.8rem;
}
.atw-selection-count {
  font-size: 0.75rem;
  font-weight: 600;
  color: var(--p-primary-color);
  padding: 0 2px;
}

/* ── DataTable overrides ─────────────────────────────────────── */
.atw :deep(.p-datatable) {
  border: 0 !important;
  border-radius: 0 !important;
}
.atw :deep(.p-datatable-thead > tr > th) {
  background: var(--p-surface-50, var(--p-surface-ground));
  font-size: 0.75rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  padding: 8px 10px;
  white-space: nowrap;
}
.atw :deep(.p-datatable-tbody > tr > td) {
  padding: 7px 10px;
  font-size: 0.825rem;
  vertical-align: middle;
}
.atw :deep(.p-datatable-paginator-bottom) {
  border-top: 1px solid var(--p-content-border-color);
  padding: 4px 10px;
}
.atw :deep(.p-paginator) {
  background: transparent;
  font-size: 0.8rem;
}
.atw :deep(.p-datatable-tbody > tr:nth-child(even)) {
  background: var(--p-surface-50, rgba(0,0,0,.02));
}
.atw :deep(.p-datatable-tbody > tr:hover) {
  background: color-mix(in srgb, var(--p-primary-color) 6%, transparent) !important;
}
.atw :deep(.atw-row-clickable) {
  cursor: pointer;
}

/* ── Leer ────────────────────────────────────────────────────── */
.atw-empty {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 24px;
  color: var(--p-text-muted-color);
  font-size: 0.825rem;
}
.atw-empty .pi { font-size: 1.25rem; opacity: 0.3; }
</style>
